/**
 * 「唤起 App」与「未安装检测」的核心逻辑。
 *
 * 浏览器没有任何 API 能告诉你「这个 App 装没装」，所以只能这样判断：
 *   1. 把页面导航到自定义 scheme（aishangdonghai://...）；
 *   2. 如果 App 装了，系统会切走 App，本页进入后台 —— 表现为
 *      document.visibilityState === 'hidden' 或触发 pagehide；
 *   3. 如果 App 没装，系统静默失败，本页一直留前台 —— 超过 timeout
 *      仍是 visible，就判定为「未安装」。
 *
 * 这是一个有意的猜测，不是精确检测，代价是：
 *   - timeout 设得短，App 启动慢的机型会被误判成「未安装」；
 *   - timeout 设得长，真的没装的用户要多等一会儿。
 * 所以 timeout 放在 share.config.mjs 里可调，默认 2500ms。
 */

import { app, shareLink, storeSchemeUrl, storeWebUrl } from '../config.js';
import { isIOS } from './platform.js';
import { normalizeInAppPath } from './share-token.js';

/** 同一次访问里，同一个 key 只真正尝试一次，避免 StrictMode 双跑 / 连点重复弹窗。 */
const attempts = new Map();

/**
 * 把 App 内路径（可选带 query）转成自定义 scheme 链接。
 *
 *   `/posts/123`                        → aishangdonghai://posts/123
 *   `/shops/goods/123` + `?shopId=456`  → aishangdonghai://shops/goods/123?shopId=456
 *
 * （expo-router 会把 host + pathname + query 一起解析成路由，
 *   所以不能用三斜杠形式）
 *
 * ⚠️ query 必须**绕过** normalizeInAppPath：那个函数会把连续斜杠折叠成单个，
 *    一旦 query 的值里出现 `//`（例如 `?from=https://x`）就会被悄悄改坏。
 *    所以只对 path 做规范化，query 原样透传。
 *
 * 为什么要带 query：商品 `/shops/goods/:id?shopId=` 和优惠券
 * `/shops/coupons/:id?shopId=` 的 shopId 是 App 路由的**必填入参**，
 * 缺了 App 会直接渲染「缺少商品或店铺参数」错误页。
 */
export function buildAppLink(inAppPath, search = '') {
  const path = normalizeInAppPath(inAppPath);
  if (!path) return `${app.scheme}://`;

  // 只接受以 ? 开头且确有内容的 query，其余（含裸 '?'）一律忽略
  const query = typeof search === 'string' && search.length > 1 && search.startsWith('?')
    ? search
    : '';

  return `${app.scheme}://${path.replace(/^\/+/, '')}${query}`;
}

/** 清掉某个 key 的尝试记录，用于「重新打开」按钮。 */
export function resetOpenAttempt(key) {
  attempts.delete(key);
}

/**
 * 尝试唤起 App。
 *
 * @param {object} options
 * @param {string} options.url      要导航到的 scheme 链接
 * @param {string} [options.key]    去重键，同一 key 只尝试一次
 * @param {number} [options.timeout]
 * @returns {Promise<'opened'|'not-installed'|'unsupported'>}
 */
export function tryOpenApp({ url, key, timeout = shareLink.openTimeoutMs }) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve('unsupported');
  }
  if (!url) return Promise.resolve('not-installed');

  const cacheKey = key || url;
  const cached = attempts.get(cacheKey);
  if (cached) return cached;

  const promise = new Promise((resolve) => {
    let settled = false;
    let blurTimer = null;

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.clearTimeout(timeoutTimer);
      window.clearTimeout(blurTimer);
    };

    const settle = (outcome) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(outcome);
    };

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') settle('opened');
    }

    function onPageHide() {
      settle('opened');
    }

    function onBlur() {
      // 失焦是弱信号：点地址栏、切输入法都会失焦。
      // 等一小会儿确认页面确实进了后台，再判成功。
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        if (document.visibilityState === 'hidden') settle('opened');
      }, 150);
    }

    function onFocus() {
      // 回到前台说明刚才只是失焦，不是被 App 接管
      window.clearTimeout(blurTimer);
    }

    // 页面已经在后台了（比如从任务切换回来立刻就调），直接算打开
    if (document.visibilityState === 'hidden') {
      resolve('opened');
      return;
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    const timeoutTimer = window.setTimeout(() => {
      // 超时前再看一眼：个别浏览器切后台的时机比定时器晚
      settle(document.visibilityState === 'hidden' ? 'opened' : 'not-installed');
    }, timeout);

    try {
      // 直接给 location 赋值，比造 <a> 点击更不容易被拦
      window.location.href = url;
    } catch {
      settle('not-installed');
    }
  });

  attempts.set(cacheKey, promise);
  return promise;
}

/**
 * 打开某个应用商店。
 *
 * iOS 直接用 https 的 App Store 链接：系统会用 App Store 打开它，
 * 比 itms-apps:// 协议稳定（协议在某些内置浏览器里会被拦）。
 *
 * Android 先用该品牌的商店协议（能直达详情页），超时没跳走再退回网页版。
 *
 * @returns {Promise<'opened'|'fallback'|'unavailable'>}
 */
export function openStore(store) {
  if (typeof window === 'undefined') return Promise.resolve('unavailable');

  const schemeUrl = storeSchemeUrl(store);
  const webUrl = storeWebUrl(store);

  // iOS，或该商店根本没有协议：直接走网页版
  if (isIOS() || !schemeUrl) {
    if (!webUrl) return Promise.resolve('unavailable');
    window.location.href = webUrl;
    return Promise.resolve('opened');
  }

  return new Promise((resolve) => {
    let done = false;

    const finish = (outcome) => {
      if (done) return;
      done = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearTimeout(timer);
      resolve(outcome);
    };

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') finish('opened');
    }

    const timer = window.setTimeout(() => {
      // 没跳走：说明本机不是这个品牌的商店，或商店没装
      if (document.visibilityState === 'hidden') {
        finish('opened');
        return;
      }
      if (webUrl) {
        window.location.href = webUrl;
        finish('fallback');
        return;
      }
      finish('unavailable');
    }, 1200);

    document.addEventListener('visibilitychange', onVisibilityChange);

    try {
      window.location.href = schemeUrl;
    } catch {
      finish('unavailable');
    }
  });
}
